if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Quick Add Enhancements
   Safe layer on top of Dawn quick-add.js.
   Keeps modal loading, product-info injection and add-to-cart intact.
   ============================================================ */

(() => {
  const QuickAddModalElement = customElements.get('quick-add-modal');
  if (!QuickAddModalElement || QuickAddModalElement.prototype.__vfCinematicPatched) return;

  QuickAddModalElement.prototype.__vfCinematicPatched = true;

  const originalShow = QuickAddModalElement.prototype.show;
  const originalHide = QuickAddModalElement.prototype.hide;
  const originalPreprocessHTML = QuickAddModalElement.prototype.preprocessHTML;

  const root = document.documentElement;

  const setQuickAddState = (modal, state, opener = null) => {
    modal.dataset.vfQuickAddState = state;

    modal.classList.toggle('vf-quick-add-loading', state === 'loading');
    modal.classList.toggle('vf-quick-add-open', state === 'open');
    modal.classList.toggle('vf-quick-add-closing', state === 'closing');
    modal.classList.toggle('vf-quick-add-error', state === 'error');

    root.classList.toggle('vf-quick-add-is-loading', state === 'loading');
    root.classList.toggle('vf-quick-add-is-open', state === 'open');

    if (opener) opener.classList.toggle('vf-quick-add-opener-loading', state === 'loading');
  };

  const ensureStatusRegion = (modal) => {
    let status = modal.querySelector('[data-vf-quick-add-status]');
    if (status) return status;

    status = document.createElement('div');
    status.className = 'visually-hidden vf-quick-add__status';
    status.setAttribute('data-vf-quick-add-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    modal.appendChild(status);
    return status;
  };

  const pulseOpener = (opener) => {
    if (!opener) return;

    opener.classList.remove('vf-quick-add-pulse');
    void opener.offsetWidth;
    opener.classList.add('vf-quick-add-pulse');

    window.setTimeout(() => {
      opener.classList.remove('vf-quick-add-pulse');
    }, 720);
  };

  QuickAddModalElement.prototype.show = function vfShow(opener) {
    const status = ensureStatusRegion(this);
    const productUrl = opener?.getAttribute('data-product-url');

    status.textContent = '';
    setQuickAddState(this, 'loading', opener);
    this.setAttribute('aria-busy', 'true');

    this.dispatchEvent(
      new CustomEvent('vf:quick-add-loading', {
        bubbles: true,
        detail: {
          opener,
          productUrl
        }
      })
    );

    const result = originalShow.call(this, opener);

    const waitForContent = () => {
      const productInfo = this.modalContent?.querySelector('product-info');

      if (!productInfo) {
        window.setTimeout(waitForContent, 80);
        return;
      }

      this.removeAttribute('aria-busy');
      setQuickAddState(this, 'open', opener);
      pulseOpener(opener);

      status.textContent = productInfo.querySelector('.product__title, h1, h2')?.textContent?.trim()
        ? `${productInfo.querySelector('.product__title, h1, h2').textContent.trim()} quick view opened.`
        : 'Quick view opened.';

      this.classList.add('vf-quick-add-ready');
      productInfo.classList.add('vf-quick-add-product-info');

      this.dispatchEvent(
        new CustomEvent('vf:quick-add-opened', {
          bubbles: true,
          detail: {
            opener,
            productUrl,
            productInfo
          }
        })
      );
    };

    window.setTimeout(waitForContent, 120);

    window.setTimeout(() => {
      if (this.dataset.vfQuickAddState !== 'loading') return;

      this.removeAttribute('aria-busy');
      setQuickAddState(this, 'error', opener);
      status.textContent = 'Quick view could not be loaded.';

      this.dispatchEvent(
        new CustomEvent('vf:quick-add-timeout', {
          bubbles: true,
          detail: {
            opener,
            productUrl
          }
        })
      );
    }, 12000);

    return result;
  };

  QuickAddModalElement.prototype.hide = function vfHide(preventFocus = false) {
    const opener = this.openedBy;

    setQuickAddState(this, 'closing', opener);

    this.dispatchEvent(
      new CustomEvent('vf:quick-add-closing', {
        bubbles: true,
        detail: {
          opener,
          preventFocus
        }
      })
    );

    const result = originalHide.call(this, preventFocus);

    window.setTimeout(() => {
      setQuickAddState(this, 'closed', opener);
      this.classList.remove('vf-quick-add-ready');

      this.dispatchEvent(
        new CustomEvent('vf:quick-add-closed', {
          bubbles: true,
          detail: {
            opener,
            preventFocus
          }
        })
      );
    }, 260);

    return result;
  };

  QuickAddModalElement.prototype.preprocessHTML = function vfPreprocessHTML(productElement) {
    if (!productElement) return originalPreprocessHTML.call(this, productElement);

    productElement.classList.add('vf-quick-add-product-info');
    productElement.setAttribute('data-vf-context', 'quick-add');

    const product = productElement.querySelector('.product');
    if (product) {
      product.classList.add('vf-quick-add-product');
      product.setAttribute('data-vf-product-layout', 'quick-add');
    }

    const mediaGallery = productElement.querySelector('media-gallery');
    if (mediaGallery) {
      mediaGallery.classList.add('vf-quick-add-gallery');
    }

    const productForm = productElement.querySelector('product-form');
    if (productForm) {
      productForm.classList.add('vf-quick-add-form');
    }

    return originalPreprocessHTML.call(this, productElement);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('quick-add-modal').forEach((modal) => {
      modal.classList.add('vf-quick-add-enhanced');
      ensureStatusRegion(modal);
    });

    document.querySelectorAll('[data-product-url]').forEach((opener) => {
      opener.classList.add('vf-quick-add-opener');
    });
  }, { once: true });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-product-url]');
    if (!opener) return;

    opener.classList.add('vf-quick-add-opener-clicked');
    window.setTimeout(() => {
      opener.classList.remove('vf-quick-add-opener-clicked');
    }, 520);
  });
})();
